import { useMemo } from 'react';
import { Flex, Select } from 'antd';
import type { SelectProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { resolveSystemRoleLabel, splitRolesByType } from '../../utils';

export type RoleTypeGroupedSelectRole = {
  id: string;
  name: string;
  isSystemRole: boolean;
};

type RoleTypeGroupedSelectOption = {
  value: string;
  label: string;
  isSystemRole: boolean;
};

function RoleTypeOptionLabel({
  label,
  isSystemRole,
}: {
  label: React.ReactNode;
  isSystemRole: boolean;
}) {
  return (
    <Flex
      align="center"
      gap={8}
      className="authorization-clone-role-select-option"
    >
      <span
        className={
          isSystemRole
            ? 'authorization-clone-role-select-option-dot authorization-clone-role-select-option-dot--system'
            : 'authorization-clone-role-select-option-dot authorization-clone-role-select-option-dot--custom'
        }
        aria-hidden
      />
      <span>{label}</span>
    </Flex>
  );
}

export type RoleTypeGroupedSelectProps = Omit<SelectProps, 'options'> & {
  roles: RoleTypeGroupedSelectRole[];
  excludeRoleId?: string;
};

export function RoleTypeGroupedSelect({
  roles,
  excludeRoleId,
  classNames,
  ...selectProps
}: RoleTypeGroupedSelectProps) {
  const { t } = useTranslation('authorization');

  const eligibleRoles = useMemo(
    () => roles.filter((role) => role.id !== excludeRoleId),
    [excludeRoleId, roles],
  );

  const selectClassNames: SelectProps['classNames'] =
    typeof classNames === 'function'
      ? classNames
      : {
          ...classNames,
          popup: {
            ...(typeof classNames?.popup === 'object'
              ? classNames.popup
              : undefined),
            root: [
              'authorization-clone-role-select',
              typeof classNames?.popup === 'object'
                ? classNames.popup.root
                : undefined,
            ]
              .filter(Boolean)
              .join(' '),
          },
        };

  const groupedOptions = useMemo(() => {
    const { systemRoles, customRoles } = splitRolesByType(eligibleRoles);

    const toOption = (
      role: RoleTypeGroupedSelectRole,
    ): RoleTypeGroupedSelectOption => ({
      value: role.id,
      label: resolveSystemRoleLabel(role.name, t),
      isSystemRole: role.isSystemRole,
    });

    const groups: Array<{
      label: React.ReactNode;
      options: RoleTypeGroupedSelectOption[];
    }> = [];

    if (systemRoles.length > 0) {
      groups.push({
        label: (
          <span className="authorization-clone-role-select-group authorization-clone-role-select-group--system">
            {t('sections.systemRoles')}
          </span>
        ),
        options: systemRoles.map(toOption),
      });
    }

    if (customRoles.length > 0) {
      groups.push({
        label: (
          <span className="authorization-clone-role-select-group authorization-clone-role-select-group--custom">
            {t('sections.customRoles')}
          </span>
        ),
        options: customRoles.map(toOption),
      });
    }

    return groups;
  }, [eligibleRoles, t]);

  return (
    <Select
      {...selectProps}
      options={groupedOptions}
      classNames={selectClassNames}
      labelRender={(labeledValue) => {
        const role = eligibleRoles.find(
          (item) => item.id === labeledValue.value,
        );
        if (!role) {
          return labeledValue.label;
        }

        return (
          <RoleTypeOptionLabel
            label={resolveSystemRoleLabel(role.name, t)}
            isSystemRole={role.isSystemRole}
          />
        );
      }}
      optionRender={(option) => {
        const role = eligibleRoles.find((item) => item.id === option.value);
        if (!role) {
          return option.label;
        }

        return (
          <RoleTypeOptionLabel
            label={option.label}
            isSystemRole={role.isSystemRole}
          />
        );
      }}
    />
  );
}
